import { useState } from 'react';

function useFloatingState(valueProp, onChangeProp) {
  const [focused, setFocused] = useState(false);
  const [internalValue, setInternalValue] = useState('');
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  function handleChange(e) {
    if (!isControlled) setInternalValue(e.target.value);
    onChangeProp?.(e);
  }

  const active = focused || String(value).length > 0;

  return { focused, setFocused, value, handleChange, active };
}

const labelStyle = (active, focused) => ({
  position: 'absolute',
  left: 0,
  top: active ? '0.4rem' : '0.9rem',
  fontFamily: 'DM Sans, Inter, sans-serif',
  fontWeight: 400,
  fontSize: active ? '0.6875rem' : '0.9375rem',
  letterSpacing: active ? '0.12em' : '0.02em',
  textTransform: active ? 'uppercase' : 'none',
  color: focused ? 'var(--accent-blue)' : 'var(--text-muted)',
  transition: 'all 0.25s cubic-bezier(0.25,0.46,0.45,0.94)',
  pointerEvents: 'none',
});

const inputStyle = (active) => ({
  width: '100%',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontFamily: 'DM Sans, Inter, sans-serif',
  fontWeight: 400,
  fontSize: '1rem',
  color: 'var(--text-primary)',
  paddingTop: active ? '1.4rem' : '0.9rem',
  paddingBottom: '0.6rem',
});

const wrapperStyle = (active, focused) => ({
  position: 'relative',
  paddingTop: active ? '0.5rem' : 0,
  borderBottom: `1px solid ${focused ? 'var(--accent-blue)' : 'var(--border-main)'}`,
  transition: 'border-color 0.25s ease',
  marginBottom: '2rem',
});

export function FloatingInput({
  label,
  type = 'text',
  name,
  required = false,
  value: valueProp,
  onChange,
}) {
  const { focused, setFocused, value, handleChange, active } = useFloatingState(valueProp, onChange);

  return (
    <div className="floating-field" style={wrapperStyle(active, focused)}>
      <label htmlFor={name} style={labelStyle(active, focused)}>
        {label}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={inputStyle(active)}
      />
    </div>
  );
}

export function FloatingTextarea({
  label,
  name,
  required = false,
  rows = 5,
  value: valueProp,
  onChange,
}) {
  const { focused, setFocused, value, handleChange, active } = useFloatingState(valueProp, onChange);

  return (
    <div className="floating-field" style={wrapperStyle(active, focused)}>
      <label htmlFor={name} style={labelStyle(active, focused)}>
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        required={required}
        rows={rows}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ ...inputStyle(active), resize: 'none' }}
      />
    </div>
  );
}

export function FloatingSelect({
  label,
  name,
  options,
  required = false,
  placeholder = 'Choose one',
  value: valueProp,
  onChange,
}) {
  const { focused, setFocused, value, handleChange } = useFloatingState(valueProp, onChange);
  // Keep the label floated so it never stacks on the placeholder option text.
  const active = true;

  return (
    <div
      className="floating-field floating-field--select"
      style={{
        ...wrapperStyle(active, focused),
        minHeight: '3.75rem',
      }}
    >
      <label htmlFor={name} style={labelStyle(active, focused)}>
        {label}
      </label>
      <select
        id={name}
        name={name}
        required={required}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="floating-field__select"
        style={{
          ...inputStyle(active),
          color: value ? 'var(--text-primary)' : 'var(--text-muted)',
          paddingTop: '1.55rem',
          paddingBottom: '0.65rem',
          paddingRight: '1.75rem',
          cursor: 'pointer',
        }}
      >
        <option value="" disabled hidden>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
